/**
 * processTreeCoverage.js
 * Pure, framework-free coverage of the BOM lines consumed by the jobs of a process, for the material rows
 * of the process Tree surface (story 118, RN48-RN60). Kept apart from `processTree.js` on purpose: the
 * sequence projection must not depend on the BOM, which is optional (Material button, RN48), asynchronous
 * (RN49) and behind its own authorization (RN51).
 *
 * The multi-level walk of the assignments is REUSED from `bomTree.js#collectJobAssignments` (RN57): the
 * tree adds up the jobs inside the sets at any depth, which is why it may disagree with the BOM section,
 * whose `remaining` only adds up the first level (`WorkflowResource.jsx`) — a deliberate, declared
 * divergence (RN58/D41), not fixed here.
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node} Node
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowResource} WorkflowResource
 * @typedef {{ linesByUUID: Map<string, { code: string, name: string, position: string, needed: number }>, assignedByUUID: Map<string, number> }} CoverageContext
 */
import { collectJobAssignments } from './bomTree'

/** RN56: the four coverage states of a BOM line — total over `assigned >= 0` (03-dominio.md §3.4). */
const COVERAGE_STATE = {
  NOT_ASSIGNED: 'NOT_ASSIGNED',
  PARTIAL: 'PARTIAL',
  COMPLETE: 'COMPLETE',
  OVERASSIGNED: 'OVERASSIGNED'
}

const toNumber = (value) => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

/**
 * RN56: `Not assigned` (0) · `Partial` (< needed) · `Complete` (= needed) · `Overassigned` (> needed).
 * Non-numeric inputs count as zero, so a corrupt quantity can never produce NaN.
 * @param {*} assigned Sum of `quantityFirstUnit` over every consuming job
 * @param {*} needed `workflowResource.quantity`
 * @returns {string} One of COVERAGE_STATE
 */
const resolveCoverageState = (assigned, needed) => {
  const assignedQuantity = toNumber(assigned)
  const neededQuantity = toNumber(needed)
  if (assignedQuantity <= 0) {
    return COVERAGE_STATE.NOT_ASSIGNED
  }
  if (assignedQuantity < neededQuantity) {
    return COVERAGE_STATE.PARTIAL
  }
  if (assignedQuantity === neededQuantity) {
    return COVERAGE_STATE.COMPLETE
  }
  return COVERAGE_STATE.OVERASSIGNED
}

/**
 * RN55/RN57/RN60: builds the coverage context of the process from the BOM lines and the nodes — every
 * line by uuid with what it needs, and what is assigned to it by every job at ANY depth. Assignments
 * without `resourceQuantityBOM.workflowResourceUUID` are silently ignored (RN60, as `bomTree.js` does).
 * @param {WorkflowResource[]} workflowResources The BOM lines (`GET workflowResource/...`)
 * @param {Node[]} nodes Top-level nodes of the (hydrated) process
 * @returns {CoverageContext}
 */
const buildCoverageContext = (workflowResources, nodes) => {
  const linesByUUID = new Map()
  ;(Array.isArray(workflowResources) ? workflowResources : []).forEach((workflowResource) => {
    if (!workflowResource || !workflowResource.uuid) {
      return
    }
    linesByUUID.set(workflowResource.uuid, {
      code: workflowResource.resource?.code || workflowResource.code || '',
      name: workflowResource.resource?.name || workflowResource.name || '',
      position: workflowResource.position === undefined || workflowResource.position === null ? '' : String(workflowResource.position),
      needed: toNumber(workflowResource.quantity)
    })
  })
  const assignedByUUID = new Map()
  const assignments = collectJobAssignments(Array.isArray(nodes) ? nodes : [])
  Object.keys(assignments).forEach((workflowResourceUUID) => {
    const total = assignments[workflowResourceUUID].reduce((sum, assignment) => sum + toNumber(assignment.quantity), 0)
    assignedByUUID.set(workflowResourceUUID, total)
  })
  return { linesByUUID, assignedByUUID }
}

/**
 * RN54/RN56.1: the material rows of one job — one per resource whose pointer resolves to a BOM line —
 * with the quantity THIS job consumes (`quantityFirstUnit`, kept as given: null means "no data", 0 means
 * zero) and the coverage state of the WHOLE line. A pointer to an unknown line is skipped (S58).
 * @param {Node} jobNode
 * @param {CoverageContext|null} coverage
 * @returns {{ workflowResourceUUID: string, code: string, name: string, position: string, quantity: number|null, state: string }[]}
 */
const resolveJobMaterialRows = (jobNode, coverage) => {
  if (!coverage || !coverage.linesByUUID) {
    return []
  }
  const resources = jobNode?.jobReference?.resources
  // RN120 (story #150) reverses RN60: a resource whose pointer does not resolve to a BOM line is NOT
  // discarded any more. Dropping it made the tree disagree with the graph, which lists everything the
  // job consumes — and the row that vanished was precisely the one worth looking at. It comes back
  // carrying its own code and name, with no verdict of its own (RN125).
  return (Array.isArray(resources) ? resources : []).map((resource, index) => {
    const workflowResourceUUID = resource?.resourceQuantityBOM?.workflowResourceUUID
    const line = workflowResourceUUID ? coverage.linesByUUID.get(workflowResourceUUID) : null
    const quantity = resource.quantityFirstUnit === null || resource.quantityFirstUnit === undefined ? null : toNumber(resource.quantityFirstUnit)
    if (!line) {
      return {
        // Positional, so two unresolved resources of the same job never collide on one key.
        key: 'r' + index,
        code: resource?.resource?.code || '',
        name: resource?.resource?.name || '',
        position: '',
        quantity,
        state: null,
        resolved: false
      }
    }
    return {
      key: workflowResourceUUID,
      code: line.code,
      name: line.name,
      position: line.position,
      quantity,
      state: resolveCoverageState(coverage.assignedByUUID.get(workflowResourceUUID), line.needed),
      resolved: true
    }
  })
}

/**
 * RN59: the Material cell of a job row — how many components it consumes and whether EVERY one of them is
 * `Complete` (`Overassigned` counts as missing). Null when it consumes nothing (the cell shows a dash).
 * @param {Node} jobNode
 * @param {CoverageContext|null} coverage
 * @returns {{ componentCount: number, hasVerdict: boolean, allComplete: boolean }|null}
 */
const summarizeJobMaterial = (jobNode, coverage) => {
  const rows = resolveJobMaterialRows(jobNode, coverage)
  if (rows.length === 0) {
    return null
  }
  // RN126: only the resources that DO resolve to a BOM line can be judged. A job that consumes nothing
  // but raw material has components to count and no verdict to give — saying "missing" about it would
  // be an accusation the data does not support.
  const withVerdict = rows.filter((row) => row.resolved)
  const hasVerdict = withVerdict.length > 0
  return {
    componentCount: rows.length,
    hasVerdict,
    allComplete: hasVerdict && withVerdict.every((row) => row.state === COVERAGE_STATE.COMPLETE)
  }
}

export {
  COVERAGE_STATE,
  resolveCoverageState,
  buildCoverageContext,
  resolveJobMaterialRows,
  summarizeJobMaterial
}
